import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Section } from '../components/ui/Common.jsx';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import ResponsiveImage from '../components/ui/ResponsiveImage.jsx';

export default function About() {
  return (
    <>
      <Helmet>
        <title>Om os | Sydthy Taxi & Turistfart ApS</title>
        <meta name="description" content="Velkommen til Sydthy Taxi & Turistfart ApS. Mere lokalt bliver det ikke. Vi tilbyder kørsel med taxi, minibusser og turistbusser i Thy og på Mors." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] w-full overflow-hidden">
        <div className="absolute inset-0">
          <ResponsiveImage
            src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/07b4da97-42be-4847-a140-4ec642b9cdfc.jpg"
            alt="Om Sydthy Taxi & Turistfart"
            className="h-full w-full object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-slate-900/60" />
        </div>
        <div className="container relative z-10 mx-auto flex h-full items-end pb-20 px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/"
              className="mb-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-blue-400 hover:text-blue-300 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
            </Link>
            <h1 className="text-5xl font-bold text-white md:text-6xl">Om os</h1>
          </div>
        </div>
      </section>

      {/* Content Section 1 */}
      <Section>
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          <div className="space-y-8 text-lg text-slate-600 leading-relaxed">
            <p className="text-xl font-medium text-slate-900">
              Sydthy Taxi & Turistfart ApS er hjemhørende i Thy og på Mors.
            </p>
            
            <div>
              <h3 className="mb-4 font-bold text-slate-900">Eksempelvis kørsel med;</h3>
              <ul className="space-y-3">
                {[
                  "Taxi",
                  "Minibusser med og uden lift",
                  "Turistbusser op til 56 personer"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="mb-4 font-bold text-slate-900">Vi tilbyder al form for kørsel i;</h3>
              <ul className="grid grid-cols-2 gap-3">
                {[
                  "Ind- og udland",
                  "Virksomheder",
                  "Foreninger",
                  "Institutioner",
                  "Udflugter",
                  "Koncerter"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="relative">
            <ResponsiveImage
              src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/40b475d2-03ca-408e-9c97-06fdbe35202b.jpg"
              alt="Sydthy Taxi & Turistfart Bus"
              className="w-full object-cover shadow-xl"
              sizes="(max-width: 1024px) 100vw, 600px"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-lg hidden md:block">
              <p className="text-2xl font-bold text-slate-900">Lokalt</p>
              <p className="text-slate-600">i Thy og på Mors</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Content Section 2 */}
      <Section className="bg-slate-50">
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          <div className="order-2 lg:order-1">
            <div className="relative max-w-md mx-auto lg:mx-0">
              <ResponsiveImage
                src="https://assets.auxodev.dk/assets/80af3c4d-4953-436f-a9f5-4abfeafc9910/8094bbf5-dfd8-48e2-a38c-d20ebdbfaadc.webp"
                alt="Palle Søndergaard"
                className="w-full object-cover shadow-xl"
                sizes="(max-width: 1024px) 100vw, 400px"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 shadow-lg w-64">
                <p className="text-xl font-bold text-slate-900">Palle Søndergaard</p>
                <p className="text-slate-600">Direktør & Chauffør</p>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 space-y-6 text-lg text-slate-600 leading-relaxed">
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl mb-6">
              Lad os tage slæbet så I kan nyde turen
            </h2>
            <p>
              Vi tilbyder planlægning af turen fra start til slut. Vi laver et oplæg til gennemførelsen af turen eks. med udflugtsmål.
            </p>
            <p>
              Og vi kan tilbyde forplejning på hele turen. En dejlig kop buskaffe, måske med et rundstykke til? En frokost i det fri eller på et hyggeligt sted? Eftermiddagskaffe med kage? En lækker menu på en hyggelig dansk spisested på vejen hjem efter en god dag? Mulighederne er mange.
            </p>
            <p className="font-medium text-slate-900 text-xl pt-4">
              Så lad os planlægge det hele - så I kan nyde turen.
            </p>
            <div className="pt-6">
              <a 
                href="/kontakt" 
                className="inline-flex items-center justify-center bg-blue-600 px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-blue-700"
              >
                Kontakt os for en uforpligtende snak
              </a>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
